import { useCallback } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  CreateClaimField,
  getCreateClaimFieldProps,
  getFieldProps,
} from '@converge/data';
import {
  FormTextField,
  FormDateField,
  PaperLayout,
  ConvergeButton,
  StackLayout,
  FormSelectField,
} from '../../../../presentation';
import {
  ClaimInput,
  ClaimStatus,
  useUpsertClaimMutation,
} from '@converge/graphql';
import { createClaimFieldsSchema } from './create.claim.validation.schema';
import { ApolloError } from '@apollo/client';
import { useConvergeSnackBar } from '../../../../contexts';
import { convergeLogger } from '@converge/config';
import './create.claim.scss';
import { useOutletContext } from 'react-router-dom';
import { InsuredEntityProps } from '../../insured';

export type CreateClaimFields = ClaimInput;
export interface Option {
  label?: string | null;
  value?: string | null;
}

export const CreateClaim = ({
  insuredUuid,
  onClose,
}: {
  insuredUuid: string;
  onClose?: () => void;
}) => {
  const formMethods = useForm<CreateClaimFields>({
    resolver: yupResolver(createClaimFieldsSchema),
    shouldUseNativeValidation: false,
  });

  const { upsertClaim, loading: isCreatingClaim } = useUpsertClaimMutation();
  const { notify } = useConvergeSnackBar();

  const { policiesOptions } = useOutletContext<InsuredEntityProps>();

  const onSubmitHandler = useCallback(
    (): SubmitHandler<CreateClaimFields> => async (fields) => {
      let claimResponse, claimResponseErrors;

      try {
        const { data, errors } = await upsertClaim({
          variables: {
            claimInput: {
              ...createClaimFieldsToVariables(fields),
              status: ClaimStatus.Open,
            },
          },
        });
        claimResponse = data;
        claimResponseErrors = errors;
      } catch (cause) {
        console.error(`Failed to create claim: ${cause}`);

        if (cause instanceof ApolloError) {
          claimResponseErrors = cause.graphQLErrors;
        }
      }

      const errorMessage = claimResponseErrors?.[0]?.message;

      if (errorMessage || !claimResponse) {
        notify(
          `Failed to create claim: ${
            errorMessage || 'No data returned by the API'
          }`,
          {
            variant: 'error',
          }
        );
        return;
      }

      notify(`Claim create successfully`, {
        variant: 'success',
      });
      onClose && onClose();
    },
    [notify, onClose, upsertClaim]
  );

  return (
    <PaperLayout>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmitHandler())}>
          <StackLayout className="create-claim">
            <StackLayout
              overflow="auto"
              direction="column"
              alignItems="stretch"
              width={400}
            >
              <CreateClaimForm policyOptions={policiesOptions} />
            </StackLayout>
            <StackLayout className="create-claim-footer">
              <ConvergeButton
                type="submit"
                label="CREATE"
                loading={isCreatingClaim}
              />
            </StackLayout>
          </StackLayout>
        </form>
      </FormProvider>
    </PaperLayout>
  );
};

const CreateClaimForm = ({ policyOptions }: { policyOptions: Option[] }) => {
  return (
    <>
      <FormSelectField
        {...getFieldProps(CreateClaimField.PolicyUuid, false, 'Policy')}
        sx={{ width: '45%' }}
        options={policyOptions}
      />
      <FormTextField
        {...getCreateClaimFieldProps(CreateClaimField.Description)}
        autoComplete={false}
        isRichText
        hideMenu
      />
      <FormDateField
        {...getCreateClaimFieldProps(CreateClaimField.DiscoveryDate)}
      />

      <FormDateField
        {...getCreateClaimFieldProps(CreateClaimField.ReportedDate)}
      />
    </>
  );
};

function createClaimFieldsToVariables(fields: CreateClaimFields): ClaimInput {
  const input: ClaimInput = {
    claimUuid: fields.claimUuid,
    policyUuid: fields.policyUuid,
    description: fields.description,
    discoveryDate: fields.discoveryDate,
    startDate: fields.startDate,
    dueDate: fields.dueDate,
    referenceId: fields.referenceId,
    reportedDate: fields.reportedDate,
  };
  convergeLogger.debug('Create claim data', input);
  return input;
}
